import { ReactNode } from 'react'
import { Typography, styled } from '@mui/material'
import LinearProgress, {
  linearProgressClasses,
} from '@mui/material/LinearProgress'
import { BottomDrawer } from '$common/bottom-drawer'
import { nothing } from '$helper/functions/utils'
import { useIsDesktopSize } from '$common/react-hooks/layout'
import { t } from '$i18n'

interface Props {
  isOpen: boolean
  icon: ReactNode
  title: ReactNode | string
  description: ReactNode | string
  actionButtons: ReactNode
  hideBackdrop?: boolean
  progress?: number
}
const ProgressWrapper = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Footer = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 12,
  width: '100%',
  borderRadius: 8,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.neutral200,
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: theme.palette.success500,
  },
}))

export const ErrorPage = ({
  isOpen,
  icon,
  title,
  description,
  actionButtons,
  hideBackdrop,
  progress,
}: Props) => {
  const isDesktop = useIsDesktopSize()
  return (
    <StyledBottomDrawer
      hideBackdrop={hideBackdrop}
      onClose={nothing}
      isOpen={isOpen}
      isFullscreen
    >
      {/* Do not remove data-is-desktop prop hence it is used in StyledBottomDrawer styles */}
      <Container data-is-desktop={isDesktop} className="error-page__container">
        <EmptyDiv />
        <Content>
          {icon}
          {typeof title === 'string' ? (
            <Typography
              mb="16px"
              mt="32px"
              textAlign="center"
              variant="h2"
              color="neutral1000"
            >
              {title}
            </Typography>
          ) : (
            title
          )}

          {typeof description === 'string' ? (
            <Typography
              textAlign="center"
              variant="paragraph1"
              color="neutral900"
            >
              {description}
            </Typography>
          ) : (
            description
          )}
        </Content>

        <Footer>
          {progress !== undefined && (
            <ProgressWrapper>
              <Typography
                color="primary700"
                textAlign="center"
                variant="subtitle3"
              >
                {progress === 100
                  ? t('forceUpdateWeb.updateReceived')
                  : t('forceUpdateWeb.receivingUpdate')}
              </Typography>
              <BorderLinearProgress
                dir="ltr"
                style={{ transform: 'rotate(0.5turn)' }}
                variant="determinate"
                value={progress}
              />
            </ProgressWrapper>
          )}
          <ButtonsContainer>{actionButtons}</ButtonsContainer>
        </Footer>
      </Container>
    </StyledBottomDrawer>
  )
}

const StyledBottomDrawer = styled(BottomDrawer)`
  z-index: ${({ theme }) => theme.zIndex.tooltip + 1};
  .error-page__container {
    width: 400px;
    max-width: 100%;
    &[data-is-desktop='true'] {
      height: 480px;
      max-height: 100%;
    }
  }
`

const Container = styled('div')`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  max-width: 400px;
  margin: auto;
`

const EmptyDiv = styled('div')`
  width: 0;
  height: 0;
`

const Content = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
`
const ButtonsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`
